// BlockList — renderar en sekvens av block med drop-zoner mellan och en
// hover-toolbar per block. Används både på top-level (av BlockCanvas) och
// rekursivt inne i varje kolumn av ett twoColumns-block.
//
// Drop-zonens "target factory" gör att samma komponent kan användas i båda
// kontexter: top-level genererar { kind: 'top', index } och kolumner genererar
// { kind: 'column', parentId, column, index }.

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, IconButton, Tooltip, alpha } from '@mui/material';
import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BlockView } from './blocks';
import { useBlockCanvas } from './canvasContext';
import { DropTarget } from './blockOps';
import { MIME_MOVE, hasDragPayload, readDragPayload } from './dnd';
import { Block, BlockId } from './types';

type TargetFactory = (index: number) => DropTarget;

type BlockListProps = {
  blocks: Block[];
  targetFactory: TargetFactory;
  // Visuell hint för tom kolumn — rendreras när blocks.length === 0
  emptyHint?: React.ReactNode;
};

const DropZone = ({
  index,
  isOver,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
}: {
  index: number;
  isOver: boolean;
  onDragEnter: (i: number) => void;
  onDragLeave: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, i: number) => void;
}) => (
  <Box
    onDragEnter={(e) => {
      if (!hasDragPayload(e)) return;
      e.stopPropagation();
      onDragEnter(index);
    }}
    onDragLeave={(e) => {
      e.stopPropagation();
      onDragLeave();
    }}
    onDragOver={(e) => {
      e.stopPropagation();
      onDragOver(e);
    }}
    onDrop={(e) => {
      e.stopPropagation();
      onDrop(e, index);
    }}
    sx={{
      position: 'relative',
      height: isOver ? 24 : 8,
      transition: 'height 120ms ease',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 12,
        right: 12,
        top: '50%',
        height: isOver ? 4 : 0,
        bgcolor: 'primary.main',
        borderRadius: 2,
        transform: 'translateY(-50%)',
        transition: 'height 120ms ease',
      },
    }}
  />
);

const BlockContainer = ({
  block,
  index,
  selected,
  canMoveUp,
  canMoveDown,
  onSelect,
  onMove,
  onDuplicate,
  onDelete,
  onDragStart,
  onContainerDragOver,
  onContainerDrop,
}: {
  block: Block;
  index: number;
  selected: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onSelect: (id: BlockId) => void;
  onMove: (id: BlockId, direction: 'up' | 'down') => void;
  onDuplicate: (id: BlockId) => void;
  onDelete: (id: BlockId) => void;
  onDragStart: (e: React.DragEvent, id: BlockId) => void;
  onContainerDragOver: (e: React.DragEvent, beforeIndex: number, afterIndex: number) => void;
  onContainerDrop: (e: React.DragEvent, beforeIndex: number, afterIndex: number) => void;
}) => {
  const { t } = useTranslation('groupMessage');
  const [hovered, setHovered] = useState(false);
  const showToolbar = hovered || selected;
  // twoColumns hanterar drops via sina inre kolumn-BlockLists — vi vill INTE
  // att den yttre wrappern också ska vara en drop target, annars skuggar den
  // de inre kolumnerna och drops "missar" rätt position.
  const isContainerLike = block.type === 'twoColumns';
  return (
    <Box
      role='button'
      tabIndex={0}
      draggable
      onDragStart={(e) => onDragStart(e, block.id)}
      onDragOver={isContainerLike ? undefined : (e) => onContainerDragOver(e, index, index + 1)}
      onDrop={isContainerLike ? undefined : (e) => onContainerDrop(e, index, index + 1)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(block.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(block.id);
        }
      }}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        border: selected ? '2px solid' : '2px solid transparent',
        borderColor: selected ? 'primary.main' : 'transparent',
        bgcolor: selected ? (theme) => alpha(theme.palette.primary.main, 0.04) : 'transparent',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&:hover': { borderColor: selected ? 'primary.main' : 'primary.light' },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 1,
        },
      }}
    >
      <BlockView block={block} />
      {showToolbar && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            display: 'flex',
            gap: 0.25,
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 2,
            border: 1,
            borderColor: 'divider',
            p: 0.25,
            zIndex: 2,
          }}
        >
          <Tooltip title={t('builder.blockActions.moveUp') as string}>
            <span>
              <IconButton size='small' disabled={!canMoveUp} onClick={() => onMove(block.id, 'up')}>
                <KeyboardArrowUpIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('builder.blockActions.moveDown') as string}>
            <span>
              <IconButton size='small' disabled={!canMoveDown} onClick={() => onMove(block.id, 'down')}>
                <KeyboardArrowDownIcon fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('builder.blockActions.duplicate') as string}>
            <IconButton size='small' onClick={() => onDuplicate(block.id)}>
              <ContentCopyIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('builder.blockActions.delete') as string}>
            <IconButton size='small' onClick={() => onDelete(block.id)} color='error'>
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export const BlockList = ({ blocks, targetFactory, emptyHint }: BlockListProps) => {
  const { t } = useTranslation('groupMessage');
  const ctx = useBlockCanvas();
  const listId = useId();

  // BlockList får aldrig renderas utan context — utveckklarfel.
  if (!ctx) {
    throw new Error('BlockList renderad utanför BlockCanvasProvider');
  }

  // Härled overIndex från canvas-context. Endast den BlockList vars listId
  // matchar activeDragTarget visar indikator — säkerställer en enda blå linje
  // åt gången även när cursor är i en nestad kolumn.
  const overIndex = ctx.activeDragTarget?.listId === listId ? ctx.activeDragTarget.index : null;
  const { setActiveDragTarget } = ctx;
  const setOverIndex = useCallback(
    (idx: number | null) => {
      if (idx === null) {
        setActiveDragTarget(null);
      } else {
        setActiveDragTarget({ listId, index: idx });
      }
    },
    [listId, setActiveDragTarget],
  );

  const handleDragOver = (e: React.DragEvent) => {
    if (!hasDragPayload(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = e.dataTransfer.types.includes(MIME_MOVE) ? 'move' : 'copy';
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setOverIndex(null);
    const payload = readDragPayload(e);
    if (!payload) return;
    const target = targetFactory(index);
    if (payload.kind === 'new') {
      ctx.onInsertBlock(payload.blockType, target);
    } else {
      ctx.onReorderBlock(payload.blockId, target);
    }
  };

  const handleBlockDragStart = (e: React.DragEvent, id: BlockId) => {
    e.stopPropagation();
    e.dataTransfer.setData(MIME_MOVE, id);
    e.dataTransfer.effectAllowed = 'move';
  };

  // När man drar över ett block — välj drop-zon ovan eller under beroende på
  // var cursor:n befinner sig. Ger mycket bredare träffyta än den tunna drop-
  // zon-stapeln mellan blocken.
  const handleContainerDragOver = (e: React.DragEvent, beforeIndex: number, afterIndex: number) => {
    if (!hasDragPayload(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = e.dataTransfer.types.includes(MIME_MOVE) ? 'move' : 'copy';
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const inUpperHalf = e.clientY < rect.top + rect.height / 2;
    setOverIndex(inUpperHalf ? beforeIndex : afterIndex);
  };

  // Drop på blocket — räkna om cursor-position så vi vet vilken zon vi ska
  // sätta in i. State hinner inte alltid uppdatera mellan dragOver och drop.
  const handleContainerDrop = (e: React.DragEvent, beforeIndex: number, afterIndex: number) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const inUpperHalf = e.clientY < rect.top + rect.height / 2;
    handleDrop(e, inUpperHalf ? beforeIndex : afterIndex);
  };

  if (blocks.length === 0) {
    return (
      <Box
        onDragEnter={(e) => {
          if (!hasDragPayload(e)) return;
          e.stopPropagation();
          setOverIndex(0);
        }}
        onDragLeave={(e) => {
          e.stopPropagation();
          if (overIndex === 0) setOverIndex(null);
        }}
        onDragOver={(e) => {
          if (!hasDragPayload(e)) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = e.dataTransfer.types.includes(MIME_MOVE) ? 'move' : 'copy';
        }}
        onDrop={(e) => {
          e.stopPropagation();
          handleDrop(e, 0);
        }}
        sx={{
          minHeight: 80,
          border: '2px dashed',
          borderColor: overIndex === 0 ? 'primary.main' : (theme) => alpha(theme.palette.text.primary, 0.15),
          borderRadius: 1,
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          fontSize: '0.85rem',
          transition: 'border-color 120ms ease',
        }}
      >
        {emptyHint ?? t('builder.canvas.dropHere')}
      </Box>
    );
  }

  return (
    <>
      <DropZone
        index={0}
        isOver={overIndex === 0}
        onDragEnter={setOverIndex}
        onDragLeave={() => setOverIndex(null)}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      />
      {blocks.map((block, i) => {
        const { canMoveUp, canMoveDown } = ctx.getMoveCapabilities(block.id);
        return (
          <Box key={block.id}>
            <BlockContainer
              block={block}
              index={i}
              selected={ctx.selectedBlockId === block.id}
              canMoveUp={canMoveUp}
              canMoveDown={canMoveDown}
              onSelect={ctx.onSelectBlock}
              onMove={ctx.onMoveBlock}
              onDuplicate={ctx.onDuplicateBlock}
              onDelete={ctx.onDeleteBlock}
              onDragStart={handleBlockDragStart}
              onContainerDragOver={handleContainerDragOver}
              onContainerDrop={handleContainerDrop}
            />
            <DropZone
              index={i + 1}
              isOver={overIndex === i + 1}
              onDragEnter={setOverIndex}
              onDragLeave={() => setOverIndex(null)}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          </Box>
        );
      })}
    </>
  );
};

export default BlockList;
